import { z } from "zod";

/*******************************************************
 * MISC
 ********************************************************/
const docSchema = z
    .array(
        z.object({
            lang: z.string(),
            name: z.string(),
            url: z.string(),
            asAtDate: z.string().optional(),
        }),
    )
    .or(z.literal("-"));

/*******************************************************
 * FUND SCHEMAS
 ********************************************************/
// ----- Base ------------------------------------------
const baseFundSchema = z.object({
    name: z.string(),
    benchmark: z.string(),
    fundPageURL: z.string(),
    assetClass: z.string(),
    capabilityStyle: z.string(),
    baseCurrency: z.string(),
    style: z.string(),
    descriptor: z.string(),
    productType: z.string(),
    domicile: z.string(),
    lgimInternalAcronym: z.string(),
    sfdrClassification: z.string(),
    prospectus: docSchema,
    aum: z.string(),
    aum__date: z.string(),
    fundLaunchDate: z.string(),
    regulatoryStatus: z.string(),
    esgReport: docSchema.optional(),
    priceType: z.string().or(z.null()).optional(),
    quarterlyIndexSummary: docSchema.optional(),
    watchlistID: z.string().optional(),
});
export type BaseFundMeta = z.infer<typeof baseFundSchema>;

const collectiveBaseFundSchema = baseFundSchema;

const pmcBaseFundSchema = baseFundSchema.omit({
    descriptor: true,
    productType: true,
    lgimInternalAcronym: true,
    sfdrClassification: true,
    prospectus: true,
    aum: true,
    aum__date: true,
});

const etfBaseFundSchema = baseFundSchema.omit({
    capabilityStyle: true,
    style: true,
});

// ----- Messages ------------------------------------------
const fundMessageSchema = z.object({
    ukPropertyMessage: z.string(),
    globalIBORReformsMessage: z.string(),
    worldwideTrustMessage: z.string(),
    worldwideTrustMessagePrivate: z.string(),
    newUnlaunchedFundsMessage: z.string(),
    secureIncomeAssetsMessage: z.string(),
    withholdingTaxNote: z.string(),
    importantInformation: z.string(),
    greenBondFundClosureMessage: z.string(),
});
const collectivesFundMessageSchema = fundMessageSchema.omit({
    secureIncomeAssetsMessage: true,
    withholdingTaxNote: true,
    greenBondFundClosureMessage: true,
});
const pmcFundMessageSchema = fundMessageSchema.pick({
    ukPropertyMessage: true,
    globalIBORReformsMessage: true,
    secureIncomeAssetsMessage: true,
    withholdingTaxNote: true,
});
const etfFundMessageSchema = fundMessageSchema.pick({
    importantInformation: true,
    greenBondFundClosureMessage: true,
});

// ----- COLLECTIVES ------------------------------------------
export const collectivesFundSchema = collectiveBaseFundSchema
    .merge(collectivesFundMessageSchema)
    .extend({
        sector: z.string(),
        isIncome: z.string(),
        priceType: z.string(),
        valuationTime: z.string(),
        settlementPeriod: z.string(),
        productType: z.string(),
        brochure: z.string(),
        reasonsWhy: z.string(),
        investorRights: docSchema,
        availabilityNote: z.string(),
        quarterlyFundUpdate: docSchema.optional(),
        fundHolidngs: docSchema.optional(),
        climateReport: docSchema.optional(),
    });

export type CollectivesFundMeta = z.infer<typeof collectivesFundSchema>;

// ----- PMC ------------------------------------------
export const pmcFundSchema = pmcBaseFundSchema
    .merge(pmcFundMessageSchema)
    .extend({
        subGrouping: z.string(),
        card_variant: z.string(),
        wrappedFund: z.literal("true").or(z.literal("-")),
        iterationDetail: z.string().optional(),
        iterationPriceSeries: z.string().optional(),
        climateReport: docSchema.optional(),
    });

export type PMCFundMeta = z.infer<typeof pmcFundSchema>;

// ----- ETF ------------------------------------------
export const etfFundSchema = etfBaseFundSchema
    .merge(etfFundMessageSchema)
    .extend({
        disruptiveTech: z.string(),
        energy: z.string(),
        buildingBlock: z.string(),
        monetaryPolicy: z.string(),
        demographic: z.string(),
        strategy: z.string(),
        assetClassStyle: z.string(),
        region: z.string(),
        investmentTheme: z.string(),
        responsible: z.string(),
        terChanges: z.string(),
        numberOfConstituents: z.string(),
        numberOfConstituents__date: z.string(),
    });
export type ETFFundMeta = z.infer<typeof etfFundSchema>;

// ----- ALL ------------------------------------------
const fund = z.object({
    currency: z.string(),
    id: z.number(),
    localId: z.string(),
    primary_shareclass: z.number().nullable(),
    share_classes: z.array(z.number()),
    type: z.literal("fund"),
});
export type BasicFundMeta = z.infer<typeof fund>;
export const fundsSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("collective"),
        data: fund.extend({ data: collectivesFundSchema }),
    }),
    z.object({
        type: z.literal("pmc"),
        data: fund.extend({ data: pmcFundSchema }),
    }),
    z.object({
        type: z.literal("etf"),
        data: fund.extend({ data: etfFundSchema }),
    }),
]);
export type FundDataWithType = z.infer<typeof fundsSchema>;

const allFundMeta = collectivesFundSchema
    .merge(pmcFundSchema)
    .merge(etfFundSchema);
export type AllFundMeta = z.infer<typeof allFundMeta>;

/*******************************************************
 * SHARE CLASS SCHEMAS
 ********************************************************/

// ----- Base ------------------------------------------
const baseShareclassSchema = z.object({
    performanceBasis: z.string(),
    launchDate: z.string(),
    shareclassPageURL: z.string(),
    shortName: z.string(),
    shareclassDescriptor: z.string().optional(),
    indexDisplayName: z.string().optional(),
    shareclassTicker: z.string().optional(),
});

const collectiveBaseShareclassSchema = baseShareclassSchema;

const pmcBaseShareclassSchema = baseShareclassSchema.omit({
    shareclassPageURL: true,
    shortName: true,
});

const etfBaseShareclassSchema = baseShareclassSchema.omit({
    performanceBasis: true,
});

// -----Cumulative ------------------------------------------
const cumulativeSchema = z.object({
    cum1m: z.string(),
    cum3m: z.string(),
    cum6m: z.string(),
    cum1y: z.string(),
    cum3y: z.string(),
    cum5y: z.string(),
    cumYtd: z.string(),
});

const collectivesCumulativeSchema = cumulativeSchema;
const pmcCumulativeSchema = z.object({
    cum3mWeeklyCloseGross: z.string(),
    cum6mWeeklyCloseGross: z.string(),
    cum1yWeeklyCloseGross: z.string(),
    cum3mWeeklyCloseGrossEur: z.string(),
    cum6mWeeklyCloseGrossEur: z.string(),
    cum1yWeeklyCloseGrossEur: z.string(),
    cumYtdWeeklyCloseGross: z.string(),
    cumYtdWeeklyCloseGrossEur: z.string(),
    cum3mDailyCloseNet: z.string(),
    cum6mDailyCloseNet: z.string(),
    cum1yDailyCloseNet: z.string(),
    cumYtdDailyCloseNet: z.string(),
    cum3mDailyCloseNetEur: z.string(),
    cum6mDailyCloseNetEur: z.string(),
    cum1yDailyCloseNetEur: z.string(),
    cumYtdDailyCloseNetEur: z.string(),
    cum3mDailyMiddayNet: z.string(),
    cum6mDailyMiddayNet: z.string(),
    cum1yDailyMiddayNet: z.string(),
    cumYtdDailyMiddayNet: z.string(),
    cum3mDailyMiddayNetEur: z.string(),
    cum6mDailyMiddayNetEur: z.string(),
    cum1yDailyMiddayNetEur: z.string(),
    cumYtdDailyMiddayNetEur: z.string(),
    cum3mWeeklyCloseNet: z.string(),
    cum6mWeeklyCloseNet: z.string(),
    cum1yWeeklyCloseNet: z.string(),
    cumYtdWeeklyCloseNet: z.string(),
    cum3mWeeklyCloseNetEur: z.string(),
    cum6mWeeklyCloseNetEur: z.string(),
    cum1yWeeklyCloseNetEur: z.string(),
    cumYtdWeeklyCloseNetEur: z.string(),
    cum3mDailyCloseGross: z.string(),
    cum6mDailyCloseGross: z.string(),
    cum1yDailyCloseGross: z.string(),
    cumYtdDailyCloseGross: z.string(),
    cum3mDailyCloseGrossEur: z.string(),
    cum6mDailyCloseGrossEur: z.string(),
    cum1yDailyCloseGrossEur: z.string(),
    cumYtdDailyCloseGrossEur: z.string(),
    cum3mDailyMiddayGross: z.string(),
    cum6mDailyMiddayGross: z.string(),
    cum1yDailyMiddayGross: z.string(),
    cumYtdDailyMiddayGross: z.string(),
    cum3mDailyMiddayGrossEur: z.string(),
    cum6mDailyMiddayGrossEur: z.string(),
    cum1yDailyMiddayGrossEur: z.string(),
    cumYtdDailyMiddayGrossEur: z.string(),
});
const etfCumulativeSchema = cumulativeSchema.omit({
    cumYtd: true,
});

// -----Annualised ------------------------------------------
const annualisedSchema = z.object({
    annly: z.string(),
    ann3y: z.string(),
    ann5y: z.string(),
});
const collectivesAnnualisedSchema = annualisedSchema.pick({
    ann3y: true,
    ann5y: true,
});
const pmcAnnualisedSchema = z.object({
    ann3yWeeklyCloseGross: z.string(),
    ann5yWeeklyCloseGross: z.string(),
    ann3yWeeklyCloseGrossEur: z.string(),
    ann5yWeeklyCloseGrossEur: z.string(),
    ann3yDailyCloseNet: z.string(),
    ann5yDailyCloseNet: z.string(),
    ann3yDailyCloseNetEur: z.string(),
    ann5yDailyCloseNetEur: z.string(),
    ann3yDailyMiddayNet: z.string(),
    ann5yDailyMiddayNet: z.string(),
    ann3yDailyMiddayNetEur: z.string(),
    ann5yDailyMiddayNetEur: z.string(),
    ann3yWeeklyCloseNet: z.string(),
    ann5yWeeklyCloseNet: z.string(),
    ann3yWeeklyCloseNetEur: z.string(),
    ann5yWeeklyCloseNetEur: z.string(),
    ann3yDailyCloseGross: z.string(),
    ann5yDailyCloseGross: z.string(),
    ann3yDailyCloseGrossEur: z.string(),
    ann5yDailyCloseGrossEur: z.string(),
    ann3yDailyMiddayGross: z.string(),
    ann5yDailyMiddayGross: z.string(),
    ann3yDailyMiddayGrossEur: z.string(),
    ann5yDailyMiddayGrossEur: z.string(),
});

const etfsAnnualisedSchema = annualisedSchema.pick({
    ann3y: true,
    ann5y: true,
});

// ----- Calendar  ------------------------------------------
const calendarSchema = z.object({
    cal1y: z.string(),
    cal2y: z.string(),
    cal3y: z.string(),
    cal4y: z.string(),
    cal5y: z.string(),
    cal6y: z.string(),
    cal7y: z.string(),
    cal8y: z.string(),
    cal9y: z.string(),
    cal10y: z.string(),
});

const collectivesCalendarSchema = calendarSchema;
const pmcCalendarSchema = z.object({
    calWeeklyCloseGross1y: z.string(),
    calWeeklyCloseGross2y: z.string(),
    calWeeklyCloseGross3y: z.string(),
    calWeeklyCloseGross4y: z.string(),
    calWeeklyCloseGross5y: z.string(),
    calWeeklyCloseGrossEur1y: z.string(),
    calWeeklyCloseGrossEur2y: z.string(),
    calWeeklyCloseGrossEur3y: z.string(),
    calWeeklyCloseGrossEur4y: z.string(),
    calWeeklyCloseGrossEur5y: z.string(),
    calDailyCloseNet1y: z.string(),
    calDailyCloseNet2y: z.string(),
    calDailyCloseNet3y: z.string(),
    calDailyCloseNet4y: z.string(),
    calDailyCloseNet5y: z.string(),
    calDailyCloseNetEur1y: z.string(),
    calDailyCloseNetEur2y: z.string(),
    calDailyCloseNetEur3y: z.string(),
    calDailyCloseNetEur4y: z.string(),
    calDailyCloseNetEur5y: z.string(),
    calDailyMiddayNet1y: z.string(),
    calDailyMiddayNet2y: z.string(),
    calDailyMiddayNet3y: z.string(),
    calDailyMiddayNet4y: z.string(),
    calDailyMiddayNet5y: z.string(),
    calDailyMiddayNetEur1y: z.string(),
    calDailyMiddayNetEur2y: z.string(),
    calDailyMiddayNetEur3y: z.string(),
    calDailyMiddayNetEur4y: z.string(),
    calDailyMiddayNetEur5y: z.string(),
    calWeeklyCloseNet1y: z.string(),
    calWeeklyCloseNet2y: z.string(),
    calWeeklyCloseNet3y: z.string(),
    calWeeklyCloseNet4y: z.string(),
    calWeeklyCloseNet5y: z.string(),
    calWeeklyCloseNetEur1y: z.string(),
    calWeeklyCloseNetEur2y: z.string(),
    calWeeklyCloseNetEur3y: z.string(),
    calWeeklyCloseNetEur4y: z.string(),
    calWeeklyCloseNetEur5y: z.string(),
    calDailyCloseGross1y: z.string(),
    calDailyCloseGross2y: z.string(),
    calDailyCloseGross3y: z.string(),
    calDailyCloseGross4y: z.string(),
    calDailyCloseGross5y: z.string(),
    calDailyCloseGrossEur1y: z.string(),
    calDailyCloseGrossEur2y: z.string(),
    calDailyCloseGrossEur3y: z.string(),
    calDailyCloseGrossEur4y: z.string(),
    calDailyCloseGrossEur5y: z.string(),
    calDailyMiddayGross1y: z.string(),
    calDailyMiddayGross2y: z.string(),
    calDailyMiddayGross3y: z.string(),
    calDailyMiddayGross4y: z.string(),
    calDailyMiddayGross5y: z.string(),
    calDailyMiddayGrossEur1y: z.string(),
    calDailyMiddayGrossEur2y: z.string(),
    calDailyMiddayGrossEur3y: z.string(),
    calDailyMiddayGrossEur4y: z.string(),
    calDailyMiddayGrossEur5y: z.string(),
});

const etfsCalendarSchema = calendarSchema;

// ----- Distributed  ------------------------------------------
const distributedSchema = z.object({
    dis1y: z.string(),
    dis2y: z.string(),
    dis3y: z.string(),
    dis4y: z.string(),
    dis5y: z.string(),
    dis6y: z.string(),
    dis7y: z.string(),
    dis8y: z.string(),
    dis9y: z.string(),
    dis10y: z.string(),
});

const collectivesDistributedSchema = distributedSchema;
const pmcDistributedSchema = z.object({
    disWeeklyCloseGross1y: z.string(),
    disWeeklyCloseGross2y: z.string(),
    disWeeklyCloseGross3y: z.string(),
    disWeeklyCloseGross4y: z.string(),
    disWeeklyCloseGross5y: z.string(),
    disWeeklyCloseGrossEur1y: z.string(),
    disWeeklyCloseGrossEur2y: z.string(),
    disWeeklyCloseGrossEur3y: z.string(),
    disWeeklyCloseGrossEur4y: z.string(),
    disWeeklyCloseGrossEur5y: z.string(),
    disDailyCloseNet1y: z.string(),
    disDailyCloseNet2y: z.string(),
    disDailyCloseNet3y: z.string(),
    disDailyCloseNet4y: z.string(),
    disDailyCloseNet5y: z.string(),
    disDailyCloseNetEur1y: z.string(),
    disDailyCloseNetEur2y: z.string(),
    disDailyCloseNetEur3y: z.string(),
    disDailyCloseNetEur4y: z.string(),
    disDailyCloseNetEur5y: z.string(),
    disDailyMiddayNet1y: z.string(),
    disDailyMiddayNet2y: z.string(),
    disDailyMiddayNet3y: z.string(),
    disDailyMiddayNet4y: z.string(),
    disDailyMiddayNet5y: z.string(),
    disDailyMiddayNetEur1y: z.string(),
    disDailyMiddayNetEur2y: z.string(),
    disDailyMiddayNetEur3y: z.string(),
    disDailyMiddayNetEur4y: z.string(),
    disDailyMiddayNetEur5y: z.string(),
    disWeeklyCloseNet1y: z.string(),
    disWeeklyCloseNet2y: z.string(),
    disWeeklyCloseNet3y: z.string(),
    disWeeklyCloseNet4y: z.string(),
    disWeeklyCloseNet5y: z.string(),
    disWeeklyCloseNetEur1y: z.string(),
    disWeeklyCloseNetEur2y: z.string(),
    disWeeklyCloseNetEur3y: z.string(),
    disWeeklyCloseNetEur4y: z.string(),
    disWeeklyCloseNetEur5y: z.string(),
    disDailyCloseGross1y: z.string(),
    disDailyCloseGross2y: z.string(),
    disDailyCloseGross3y: z.string(),
    disDailyCloseGross4y: z.string(),
    disDailyCloseGross5y: z.string(),
    disDailyCloseGrossEur1y: z.string(),
    disDailyCloseGrossEur2y: z.string(),
    disDailyCloseGrossEur3y: z.string(),
    disDailyCloseGrossEur4y: z.string(),
    disDailyCloseGrossEur5y: z.string(),
    disDailyMiddayGross1y: z.string(),
    disDailyMiddayGross2y: z.string(),
    disDailyMiddayGross3y: z.string(),
    disDailyMiddayGross4y: z.string(),
    disDailyMiddayGross5y: z.string(),
    disDailyMiddayGrossEur1y: z.string(),
    disDailyMiddayGrossEur2y: z.string(),
    disDailyMiddayGrossEur3y: z.string(),
    disDailyMiddayGrossEur4y: z.string(),
    disDailyMiddayGrossEur5y: z.string(),
});

const etfsDistributedSchema = distributedSchema.pick({
    dis1y: true,
    dis2y: true,
    dis3y: true,
    dis4y: true,
    dis5y: true,
});

// ----- Documents ------------------------------------------
const documentsSchema = z.object({
    factSheet: docSchema,
    kiid: docSchema,
    kid: docSchema,
    memberCommunication: docSchema,
    esgReportSC: docSchema,
});
const collectivesDocumentsSchema = documentsSchema.pick({
    factSheet: true,
    kiid: true,
    kid: true,
});
const pmcDocumentsSchema = documentsSchema.pick({
    factSheet: true,
    kid: true,
    memberCommunication: true,
    esgReportSC: true,
});

const etfsDocumentsSchema = documentsSchema.pick({
    factSheet: true,
    kiid: true,
    kid: true,
});

// ----- Mid-price ------------------------------------------
const midPriceSchema = z.object({
    midPrice: z.string(),
    midPrice__date: z.string(),
});

// ----- COLLECTIVES ------------------------------------------
export const collectivesShareClassSchema = collectiveBaseShareclassSchema
    .merge(collectivesAnnualisedSchema)
    .merge(collectivesCalendarSchema)
    .merge(collectivesCumulativeSchema)
    .merge(collectivesDistributedSchema)
    .merge(collectivesDocumentsSchema)
    .merge(midPriceSchema)
    .extend({
        isin: z.string(),
        sedol: z.string(),
        bloomberg: z.string(),
        cusip: z.string(),
        citi: z.string(),
        wkn: z.string(),
        accInc: z.string(),
        ocf: z.string(),
        mex: z.string(),
        launchDateGerman: z.string(),
        benchmarkBloomberg: z.string(),
        benchmarkShareClass: z.string(),
        bidChange: z.string(),
        bidChange__date: z.string(),
        midChange: z.string(),
        midChange__date: z.string(),
        bidPrice: z.string(),
        bidPrice__date: z.string(),
        offerPrice: z.string(),
        offerPrice__date: z.string(),
        midPriceGerman: z.string(),
        midPriceGerman__date: z.string(),
        mtmNAV: z.string(),
        mtmNAV__date: z.string(),
        differenceNAV: z.string(),
        differenceNAV__date: z.string(),
        lvNAV: z.string(),
        lvNAV__date: z.string(),
        accNAV: z.string(),
        accNAV__date: z.string(),
        EURlvNAV: z.string(),
        EURlvNAV__date: z.string(),
        EURmtmNAV: z.string(),
        EURmtmNAV__date: z.string(),
        EURdifferenceNAV: z.string(),
        EURdifferenceNAV__date: z.string(),
        minInvestment: z.string().optional(),
    });

export type CollectivesShareClassMeta = z.infer<
    typeof collectivesShareClassSchema
>;

// ----- PMCS ------------------------------------------
const pmcsShareClassSchema = pmcBaseShareclassSchema
    .merge(pmcCalendarSchema)
    .merge(pmcCumulativeSchema)
    .merge(pmcDistributedSchema)
    .merge(pmcAnnualisedSchema)
    .merge(pmcDocumentsSchema)
    .extend({
        iterationPageURL: z.string(),
        iterationBenchmarkDetail: z.string(),
        availableUK: z.string(),
        iterationNetWHT: z.string(),
        iterationWithCharges: z.string(),
        iterationHedgedGBP: z.string(),
        iterationHedgedEUR: z.string(),
        olabCurrencyGBP: z.string(),
        olabCurrencyEUR: z.string(),
        iterationName: z.string(),
        iterationDetail: z.string(),
        iterationPriceSeries: z.string(),
        scopeCode: z.string(),
        shareclassBenchmark: z.string(),
        currency: z.string(),
        dailyMidday: z.string(),
        dailyClose: z.string(),
        weeklyClose: z.string(),
        iterationSearchName: z.string(),
        availableDB: z.string(),
        availableDC: z.string(),
        availableOLAB: z.string(),
        priceCurrency: z.string(),
        priceDailyMiddayDealing: z.string(),
        priceDailyMiddayDealing__date: z.string(),
        priceDailyCloseMid: z.string(),
        priceDailyCloseMid__date: z.string(),
        priceDailyCloseDealing: z.string(),
        priceDailyCloseDealing__date: z.string(),
        priceWeeklyBidEur__date: z.string(),
        priceWeeklyMidEur__date: z.string(),
        priceWeeklyOfferEur: z.string(),
        priceWeeklyOfferEur__date: z.string(),
        priceDailyMiddayMidEur: z.string(),
        priceDailyMiddayMidEur__date: z.string(),
        priceDailyMiddayDealingEur: z.string(),
        priceDailyMiddayDealingEur__date: z.string(),
        priceDailyCloseMidEur: z.string(),
        priceDailyCloseMidEur__date: z.string(),
        priceDailyCloseDealingEur: z.string(),
        priceDailyCloseDealingEur__date: z.string(),
        priceWeeklyBid: z.string(),
        priceWeeklyBid__date: z.string(),
        priceWeeklyMid: z.string(),
        priceWeeklyMid__date: z.string(),
        priceWeeklyOffer: z.string(),
        priceWeeklyOffer__date: z.string(),
        priceDailyMiddayMid: z.string(),
        priceDailyMiddayMid__date: z.string(),
    });

export type PMCShareClassMeta = z.infer<typeof pmcsShareClassSchema>;

// ----- ETFS ------------------------------------------

const etfsShareClassSchema = etfBaseShareclassSchema
    .merge(etfCumulativeSchema)
    .merge(etfsAnnualisedSchema)
    .merge(etfsDocumentsSchema)
    .merge(etfsDistributedSchema)
    .merge(etfsCalendarSchema)
    .merge(midPriceSchema)
    .extend({
        ter: z.string(),
        shareclassISIN: z.string(),
        shareclassCurrency: z.string(),
        indexTicker: z.string(),
        netAssets: z.string(),
        netAssets__date: z.string(),
        netAssetValueFund: z.string(),
        netAssetValueFund__date: z.string(),
        midChange: z.string(),
        midChange__date: z.string(),
    });

export type ETFShareClassMeta = z.infer<typeof etfsShareClassSchema>;

// ----- ALL ------------------------------------------
const shareClass = z.object({
    id: z.number(),
    localId: z.string(),
    currency: z.string(),
    parentId: z.string(),
    type: z.literal("shareclass"),
});
export const shareClassSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("collective"),
        id: z.number().optional(),
        data: shareClass.extend({ data: collectivesShareClassSchema }),
    }),
    z.object({
        type: z.literal("pmc"),
        id: z.number().optional(),
        data: shareClass.extend({ data: pmcsShareClassSchema }),
    }),
    z.object({
        type: z.literal("etf"),
        id: z.number().optional(),
        data: shareClass.extend({ data: etfsShareClassSchema }),
    }),
]);
export type ShareClassDataWithType = z.infer<typeof shareClassSchema>;

export const shareClassGuard = (
    x: ShareClassDataWithType | undefined,
): x is ShareClassDataWithType => Boolean(x);

const allShareClassMeta = collectivesShareClassSchema
    .merge(pmcsShareClassSchema)
    .merge(etfsShareClassSchema);
export type AllShareClassMeta = z.infer<typeof allShareClassMeta>;

/*******************************************************
 * LISTING SCHEMAS
 ********************************************************/

export const listingSchema = z.object({
    bloomberg: z.string(),
    exchangeName: z.string(),
    isin: z.string(),
    listingCurrency: z.string(),
    listingTicker: z.string(),
    sedol: z.string(),
    wkn: z.string(),
    listingDate: z.string(),
});

export type Listing = z.infer<typeof listingSchema>;

export const topLevelListingsSchema = z.object({
    type: z.literal("listing"),
    id: z.number(),
    localId: z.string(),
    currency: z.string(),
    parentId: z.string(),
    data: listingSchema,
});

export type TopLevelListingObj = z.infer<typeof topLevelListingsSchema>;
